import { useState, useEffect, useContext, useRef } from "react";
import {
  Card,
  Button,
  Progress,
  Tag,
  message,
  Space,
  Typography,
  Spin,
  Popconfirm,
  Upload,
  Alert,
} from "antd";
import {
  StopOutlined,
  CloudUploadOutlined,
  InboxOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Context } from "../../context";
import { useTranscoding } from "../../TranscodingContext";
import { stripTrailingSlash } from "../../utils";

const TRANSCODE_SECRET =
  process.env.REACT_APP_TRANSCODE_SECRET || "umeshchand";

const DEFAULT_CLIENT_NAME =
  process.env.REACT_APP_TRANSCODE_CLIENT_NAME || "minipix-laptop";

const DEFAULT_WORKER_NAME =
  process.env.REACT_APP_TRANSCODE_WORKER_NAME || "minipix-laptop";

/** e.g. https://api.minipixapp.info/ → https://api.minipixapp.info/transcoding */
function transcodingApiBase(transcodeUrl) {
  if (!transcodeUrl) return "https://api.minipix.in.net/transcoding";
  try {
    const raw = transcodeUrl.startsWith("http")
      ? transcodeUrl
      : `https://${transcodeUrl}`;
    const u = new URL(stripTrailingSlash(raw));
    return `${u.origin}/transcoding`;
  } catch {
    return "https://api.minipix.in.net/transcoding";
  }
}

function formatUploadValidationError(body) {
  if (!body || typeof body !== "object") return null;
  if (typeof body.detail === "string") return body.detail;
  if (Array.isArray(body.detail)) {
    return body.detail
      .map((d) => (d && typeof d === "object" ? d.msg || d.message : String(d)))
      .filter(Boolean)
      .join("; ");
  }
  if (body.detail && typeof body.detail === "object") {
    return JSON.stringify(body.detail);
  }
  if (body.message) return String(body.message);
  return null;
}

const TRANSCODE_STEP_LABELS = {
  extracting_audio: "Extracting Audio",
  transcoding_1080p: "Transcoding 1080p",
  transcoding_720p: "Transcoding 720p",
  transcoding_480p: "Transcoding 480p",
  transcoding_360p: "Transcoding 360p",
  packaging: "Finalizing Video",
  audio_fragmentation: "Fragmenting Audio",
  video_fragmentation: "Fragmenting Video",
  claim: "Starting Job",
};

function humanizeTranscodeStep(raw) {
  if (raw == null || raw === "") return "";
  const key = String(raw).trim();
  if (TRANSCODE_STEP_LABELS[key]) return TRANSCODE_STEP_LABELS[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Map GET /transcoding/jobs/by-content job payload → global transcoding job shape. */
function jobFromPrecheckApi(apiJob, contentIdStr) {
  if (!apiJob) return null;
  const id = apiJob.job_id ?? apiJob.jobId;
  if (!id) return null;
  const out = apiJob.output_dir ?? apiJob.outputDir;
  const cid = apiJob.content_id ?? apiJob.contentId ?? contentIdStr;
  const prog = apiJob.progress;
  const n = typeof prog === "number" ? prog : Number(prog);
  return {
    jobId: String(id),
    contentId: String(cid),
    status: apiJob.status != null ? String(apiJob.status) : "",
    progress: !Number.isNaN(n) && Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0,
    step: apiJob.step != null ? String(apiJob.step) : "",
    outputDir: out != null && out !== "" ? String(out) : "",
    error: apiJob.error != null && apiJob.error !== "" ? String(apiJob.error) : "",
  };
}

function getUIState(res) {
  if (!res || !res.success) {
    return { type: "NEW", job: null };
  }

  const job =
    res.job ||
    (res.job_id
      ? {
          job_id: res.job_id,
          content_id: res.content_id,
          status: res.status,
          step: res.step,
          progress: res.progress,
          output_dir: res.output_dir,
          error: res.error,
        }
      : null);

  if (!job || !(job.job_id || job.jobId)) {
    return { type: "NEW", job: null };
  }

  const status = String(job.status || "").toUpperCase();

  if (status === "COMPLETED") return { type: "COMPLETED", job };
  if (status === "RUNNING") return { type: "RUNNING", job };
  if (status === "QUEUED") return { type: "QUEUED", job };
  if (status === "FAILED" || status === "KILLED") return { type: "FAILED", job };

  return { type: "NEW", job: null };
}

function apiJobShapeFromGlobal(globalJob) {
  if (!globalJob) return null;
  return {
    job_id: globalJob.jobId,
    content_id: globalJob.contentId,
    status: globalJob.status,
    step: globalJob.step,
    progress: globalJob.progress,
    output_dir: globalJob.outputDir,
    error: globalJob.error,
  };
}

/** Shimmer overlays on Progress + interactive card chrome (UI-only). */
const VST_UI_CSS = `
@keyframes vst-gleam-a {
  0% { transform: translateX(-70%) translateY(-50%); opacity: 0.25; }
  40% { opacity: 1; }
  100% { transform: translateX(240%) translateY(-50%); opacity: 0.2; }
}
@keyframes vst-gleam-b {
  0% { transform: translateX(-100%) translateY(-50%); opacity: 0; }
  45% { opacity: 0.85; }
  100% { transform: translateX(280%) translateY(-50%); opacity: 0; }
}
@keyframes vst-gleam-spark {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
}
@keyframes vst-bar-glow {
  0%, 100% { filter: drop-shadow(0 0 3px rgba(24, 144, 255, 0.25)); }
  50% { filter: drop-shadow(0 0 10px rgba(24, 144, 255, 0.55)); }
}
@keyframes vst-success-bar-glow {
  0%, 100% { filter: drop-shadow(0 0 2px rgba(82, 196, 26, 0.35)); }
  50% { filter: drop-shadow(0 0 12px rgba(115, 209, 61, 0.45)); }
}
@keyframes vst-success-gleam {
  0% { transform: translateX(-90%) translateY(-50%); }
  100% { transform: translateX(220%) translateY(-50%); }
}
@keyframes vst-track-sweep {
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}
@keyframes vst-sheen-sweep {
  0% { transform: translateX(-120%) skewX(-12deg); opacity: 0; }
  15% { opacity: 0.9; }
  100% { transform: translateX(220%) skewX(-12deg); opacity: 0; }
}
@keyframes vst-gleam-c {
  0% { transform: translateX(-40%) translateY(-50%) scaleX(0.6); opacity: 0; }
  35% { opacity: 0.75; }
  100% { transform: translateX(320%) translateY(-50%) scaleX(1); opacity: 0; }
}
.vst-interactive-card.ant-card {
  transition: box-shadow 0.28s cubic-bezier(0.4, 0, 0.2, 1), transform 0.22s ease, border-color 0.2s ease, background-color 0.25s ease;
  border-radius: 12px;
}
.vst-interactive-card.ant-card:hover {
  box-shadow: 0 10px 28px rgba(235, 47, 150, 0.12), 0 4px 14px rgba(0, 0, 0, 0.06);
  border-color: #f3d4e0;
}
.vst-interactive-card.ant-card:active {
  transform: scale(0.992);
  border-color: #adc6ff;
  background-color: #fafcff;
  box-shadow: 0 4px 22px rgba(24, 144, 255, 0.2), 0 0 0 1px rgba(24, 144, 255, 0.14) inset, 0 2px 10px rgba(0, 0, 0, 0.05);
}
.vst-meta-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
}
.vst-meta-label {
  flex-shrink: 0;
  width: 4.5rem;
}
.vst-meta-value {
  flex: 1;
  min-width: 0;
}
.vst-meta-value .ant-typography {
  margin-bottom: 0 !important;
}
.vst-meta-value code,
.vst-meta-row .ant-tag {
  max-width: 100%;
}
.vst-meta-value .ant-typography-copy {
  flex-shrink: 0;
}
.vst-interactive-btn.ant-btn {
  transition: box-shadow 0.24s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s ease, border-color 0.2s ease, filter 0.2s ease, background-color 0.2s ease;
  border-radius: 10px;
}
.vst-interactive-btn.ant-btn:not(:disabled):not(.ant-btn-loading):hover {
  box-shadow: 0 8px 22px rgba(235, 47, 150, 0.12), 0 2px 10px rgba(0, 0, 0, 0.06);
}
.vst-interactive-btn.ant-btn-primary:not(:disabled):not(.ant-btn-loading):hover {
  box-shadow: 0 8px 24px rgba(24, 144, 255, 0.22), 0 2px 10px rgba(0, 0, 0, 0.05);
}
.vst-interactive-btn.ant-btn-dangerous:not(:disabled):not(.ant-btn-loading):hover {
  box-shadow: 0 8px 22px rgba(255, 77, 79, 0.18), 0 2px 10px rgba(0, 0, 0, 0.05);
}
.vst-interactive-btn.ant-btn:not(:disabled):not(.ant-btn-loading):active {
  transform: scale(0.985);
  box-shadow: 0 4px 18px rgba(24, 144, 255, 0.2), 0 0 0 1px rgba(24, 144, 255, 0.12) inset, 0 2px 8px rgba(0, 0, 0, 0.05);
}
.vst-interactive-btn.ant-btn-primary:not(:disabled):not(.ant-btn-loading):active {
  filter: brightness(0.97);
}
.vst-interactive-btn.ant-btn-dangerous:not(:disabled):not(.ant-btn-loading):active {
  box-shadow: 0 4px 18px rgba(255, 77, 79, 0.22), 0 0 0 1px rgba(255, 77, 79, 0.12) inset, 0 2px 8px rgba(0, 0, 0, 0.05);
}
.vst-interactive-btn.ant-btn-link:not(:disabled):not(.ant-btn-loading):active {
  transform: scale(0.99);
}
.vst-progress-wrap {
  position: relative;
  width: 100%;
  padding: 6px 0 4px;
}
.vst-progress-wrap .ant-progress-inner {
  position: relative;
}
.vst-progress-wrap.is-active .ant-progress-inner {
  background: linear-gradient(90deg, #ececec 0%, #f7f7f7 35%, #e6e6e6 55%, #f2f2f2 100%) !important;
  background-size: 220% 100%;
  animation: vst-track-sweep 2.6s ease-in-out infinite;
}
.vst-progress-wrap.is-active .ant-progress-bg {
  animation: vst-bar-glow 2.2s ease-in-out infinite;
}
.vst-progress-shimmer {
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 14px;
  margin-top: -7px;
  overflow: hidden;
  border-radius: 100px;
}
.vst-progress-shimmer::before,
.vst-progress-shimmer::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 42%;
  height: 10px;
  margin-top: -5px;
  border-radius: 100px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.95), transparent);
}
.vst-progress-shimmer::before {
  animation: vst-gleam-a 1.45s ease-in-out infinite;
}
.vst-progress-shimmer::after {
  width: 55%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
  animation: vst-gleam-b 1.95s ease-in-out infinite;
}
.vst-progress-shimmer-extra {
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 14px;
  margin-top: -7px;
  overflow: hidden;
  border-radius: 100px;
}
.vst-progress-shimmer-extra::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 30%;
  height: 8px;
  margin-top: -4px;
  border-radius: 100px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.65), transparent);
  animation: vst-gleam-c 2.8s ease-in-out infinite;
}
.vst-progress-sheen {
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 18px;
  margin-top: -9px;
  overflow: hidden;
  border-radius: 100px;
}
.vst-progress-sheen::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 38%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
  animation: vst-sheen-sweep 2.2s ease-in-out infinite;
}
.vst-progress-sparkle {
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 14px;
  margin-top: -7px;
  background: radial-gradient(circle at 18% 50%, rgba(255,255,255,0.7) 0%, transparent 42%),
    radial-gradient(circle at 50% 50%, rgba(255,255,255,0.25) 0%, transparent 38%),
    radial-gradient(circle at 82% 50%, rgba(255,255,255,0.45) 0%, transparent 40%);
  animation: vst-gleam-spark 1.15s ease-in-out infinite;
  border-radius: 100px;
}
.vst-progress-wrap-success {
  position: relative;
  width: 100%;
  padding: 6px 0 4px;
}
.vst-progress-wrap-success .ant-progress-bg {
  box-shadow: 0 0 0 1px rgba(82, 196, 26, 0.28), 0 0 14px rgba(82, 196, 26, 0.22);
  animation: vst-success-bar-glow 2.6s ease-in-out infinite;
}
.vst-progress-wrap-success .ant-progress-inner {
  background: linear-gradient(90deg, #f4fbf0 0%, #fafefa 45%, #eef9e8 100%) !important;
  background-size: 200% 100%;
  animation: vst-track-sweep 3.2s ease-in-out infinite;
}
.vst-progress-shimmer-success {
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 14px;
  margin-top: -7px;
  overflow: hidden;
  border-radius: 100px;
}
.vst-progress-shimmer-success::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 45%;
  height: 10px;
  margin-top: -5px;
  border-radius: 100px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.85), transparent);
  animation: vst-success-gleam 1.65s ease-in-out infinite;
}
.vst-progress-shimmer-success::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 28%;
  height: 10px;
  margin-top: -5px;
  border-radius: 100px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
  animation: vst-success-gleam 1.1s ease-in-out infinite reverse;
}
.vst-progress-shimmer-success-extra {
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 14px;
  margin-top: -7px;
  overflow: hidden;
  border-radius: 100px;
}
.vst-progress-shimmer-success-extra::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 36%;
  height: 8px;
  margin-top: -4px;
  border-radius: 100px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
  animation: vst-gleam-c 2.4s ease-in-out infinite;
}
`;

const VerticalShortTranscodingUpload = ({ upload, onRefresh }) => {
  const { token, transcodeUrl } = useContext(Context);
  const {
    job: globalJob,
    setJob,
    clearJob,
    finalizeClientKill,
    registerJobCompleteHandler,
  } = useTranscoding();

  const [videoFile, setVideoFile] = useState(null);
  const [filename, setFilename] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isKilling, setIsKilling] = useState(false);
  const [uiState, setUiState] = useState({ type: "NEW", job: null });
  const [precheckLoading, setPrecheckLoading] = useState(false);

  const globalJobRef = useRef(globalJob);
  globalJobRef.current = globalJob;

  const latestContentIdRef = useRef("");
  latestContentIdRef.current = upload?._id ? String(upload._id) : "";

  const isThisUploadJob =
    globalJob &&
    upload?._id &&
    String(globalJob.contentId) === String(upload._id);

  const precheckJobNormalized =
    upload?._id && uiState.job ? jobFromPrecheckApi(uiState.job, upload._id) : null;

  const activeJob =
    isThisUploadJob && globalJob
      ? globalJob
      : precheckJobNormalized &&
          (uiState.type === "COMPLETED" ||
            uiState.type === "FAILED" ||
            ((uiState.type === "QUEUED" || uiState.type === "RUNNING") && !isThisUploadJob))
        ? precheckJobNormalized
        : null;

  const jobId = activeJob?.jobId ?? null;
  const status = activeJob?.status != null ? String(activeJob.status) : null;
  const step = activeJob?.step != null ? String(activeJob.step) : "";
  const progress =
    typeof activeJob?.progress === "number" ? activeJob.progress : 0;
  const outputDir =
    activeJob?.outputDir != null && activeJob.outputDir !== ""
      ? String(activeJob.outputDir)
      : "";
  const error =
    activeJob?.error != null && activeJob.error !== "" ? String(activeJob.error) : "";

  useEffect(() => {
    registerJobCompleteHandler(() => {
      if (typeof onRefresh === "function") onRefresh();
    });
    return () => registerJobCompleteHandler(null);
  }, [onRefresh, registerJobCompleteHandler]);

  useEffect(() => {
    if (!upload?._id) {
      setUiState({ type: "NEW", job: null });
      setPrecheckLoading(false);
      return;
    }

    const cidStr = String(upload._id);
    const g = globalJobRef.current;
    if (g?.contentId != null && String(g.contentId) !== cidStr) {
      clearJob();
    }

    let cancelled = false;
    setPrecheckLoading(true);

    const run = async () => {
      try {
        const base = String(transcodeUrl || "https://api.minipixapp.info/").replace(/\/$/, "");
        const res = await fetch(
          `${base}/transcoding/jobs/by-content/${encodeURIComponent(cidStr)}`,
          {
            headers: {
              Accept: "application/json",
              "x-transcode-secret": TRANSCODE_SECRET,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );

        const data = await res.json().catch(() => ({}));
        if (cancelled || latestContentIdRef.current !== cidStr) return;

        if (!res.ok) {
          console.warn("by-content pre-check HTTP error", res.status);
          setUiState({ type: "NEW", job: null });
          return;
        }

        const state = getUIState(data);
        setUiState(state);

        const gNow = globalJobRef.current;

        if (state.type === "RUNNING" || state.type === "QUEUED") {
          const mapped = jobFromPrecheckApi(state.job, cidStr);
          if (mapped) {
            setJob({
              ...mapped,
              contentTitle: upload?.title ? String(upload.title) : "",
            });
          }
        } else if (state.type === "COMPLETED" || state.type === "FAILED") {
          if (gNow && String(gNow.contentId) === cidStr) {
            clearJob();
          }
        } else if (state.type === "NEW") {
          if (gNow && String(gNow.contentId) === cidStr) {
            clearJob();
          }
        }
      } catch (err) {
        console.error("Job fetch failed", err);
        if (!cancelled && latestContentIdRef.current === cidStr) {
          setUiState({ type: "NEW", job: null });
        }
      } finally {
        if (!cancelled && latestContentIdRef.current === cidStr) {
          setPrecheckLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [upload?._id, transcodeUrl, token, setJob, clearJob]);

  useEffect(() => {
    if (!upload?._id || !isThisUploadJob || !globalJob) return;
    const s = String(globalJob.status || "").toUpperCase();
    if (s === "COMPLETED") {
      setUiState({ type: "COMPLETED", job: apiJobShapeFromGlobal(globalJob) });
    } else if (s === "FAILED" || s === "KILLED") {
      setUiState({ type: "FAILED", job: apiJobShapeFromGlobal(globalJob) });
    } else if (s === "RUNNING") {
      setUiState({ type: "RUNNING", job: apiJobShapeFromGlobal(globalJob) });
    } else if (s === "QUEUED") {
      setUiState({ type: "QUEUED", job: apiJobShapeFromGlobal(globalJob) });
    }
  }, [upload?._id, isThisUploadJob, globalJob?.jobId, globalJob?.status]);

  const getStatusTagColor = (s) => {
    if (!s) return "default";
    const u = String(s).toUpperCase();
    if (u === "QUEUED") return "orange";
    if (u === "RUNNING") return "blue";
    if (u === "COMPLETED") return "green";
    if (u === "FAILED") return "red";
    if (u === "KILLED") return "volcano";
    return "default";
  };

  const onUploadAndStart = async () => {
    const contentId = upload?._id;
    if (!videoFile || !contentId) {
      message.error("Select a video file and a vertical series first");
      return;
    }

    if (precheckLoading) {
      message.warning("Still checking for an existing job for this series…");
      return;
    }

    if (!(uiState.type === "NEW" || uiState.type === "FAILED")) {
      message.warning("This series already has a transcoding job.");
      return;
    }

    clearJob();

    const form = new FormData();
    const safeName = videoFile.name || "video.mp4";
    form.append("file", videoFile, safeName);
    form.append("filename", safeName);
    form.append("content_id", String(contentId));
    form.append("content_type", "episodes");
    form.append(
      "category",
      upload?.category ||
        process.env.REACT_APP_TRANSCODE_UPLOAD_CATEGORY ||
        "series"
    );
    form.append("client_name", DEFAULT_CLIENT_NAME);
    form.append("worker_name", DEFAULT_WORKER_NAME);
    form.append("api_base", transcodingApiBase(transcodeUrl));

    setIsUploading(true);
    try {
      const res = await fetch(`${transcodeUrl}/transcoding/upload/start`, {
        method: "POST",
        headers: {
          "x-transcode-secret": TRANSCODE_SECRET,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: form,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const validationMsg = formatUploadValidationError(data);
        message.error(
          validationMsg ||
            data.message ||
            `Upload start failed (${res.status})`
        );
        return;
      }

      if (!data.success) {
        message.error(
          formatUploadValidationError(data) ||
            data.message ||
            "Upload start not successful"
        );
        return;
      }

      const id = data.job_id;
      if (!id) {
        message.error("No job_id in response");
        return;
      }

      setJob({
        jobId: id,
        contentId: String(contentId),
        status: data.status != null ? String(data.status) : "QUEUED",
        progress: 0,
        step: "starting",
        contentTitle: upload?.title ? String(upload.title) : "",
      });

      const stUp = String(data.status != null ? data.status : "QUEUED").toUpperCase();
      setUiState({
        type: stUp === "RUNNING" ? "RUNNING" : "QUEUED",
        job: {
          job_id: id,
          content_id: String(contentId),
          status: data.status != null ? String(data.status) : "QUEUED",
          step: "starting",
          progress: 0,
        },
      });

      message.success("Upload complete — job queued");
    } catch (err) {
      console.error(err);
      message.error("Upload / job creation failed");
    } finally {
      setIsUploading(false);
    }
  };

  const onKillJob = async () => {
    if (!jobId) return;

    setIsKilling(true);
    try {
      const base = String(transcodeUrl || "https://api.minipixapp.info/").replace(/\/$/, "");
      const res = await fetch(`${base}/transcoding/jobs/${jobId}/kill`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "x-transcode-secret": TRANSCODE_SECRET,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        message.error(
          formatUploadValidationError(data) ||
            data.message ||
            `Kill request failed (${res.status})`
        );
        return;
      }

      if (data && data.success === false) {
        message.error(formatUploadValidationError(data) || data.message || "Kill not accepted");
        return;
      }

      finalizeClientKill(String(data.status ?? data.job?.status ?? "KILLED"));
      message.success("Stop request sent");
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error(err);
      message.error("Failed to stop transcoding");
    } finally {
      setIsKilling(false);
    }
  };

  const uploadAllowed = uiState.type === "NEW" || uiState.type === "FAILED";
  const uploadDisabled =
    precheckLoading || !uploadAllowed || !videoFile || !upload?._id;

  const statusUpper = status != null ? String(status).toUpperCase() : "";

  const killButtonVisible =
    Boolean(jobId) && (statusUpper === "QUEUED" || statusUpper === "RUNNING");

  const uploadFileList = videoFile
    ? [
        {
          uid: "video-1",
          name: filename || videoFile.name || "video.mp4",
          status: "done",
        },
      ]
    : [];

  const humanizedStep = humanizeTranscodeStep(step);

  const uploadFormInner = (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {!upload?._id ? (
        <Alert
          type="info"
          showIcon
          message="Select a vertical series in the dropdown above to enable upload."
        />
      ) : null}

      {upload?._id && precheckLoading ? (
        <Alert
          type="info"
          showIcon
          message="Checking for an existing transcoding job for this series…"
        />
      ) : null}

      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Drag and drop a file here, or click to browse. MP4 recommended.
      </Typography.Paragraph>

      <Upload.Dragger
        name="file"
        multiple={false}
        maxCount={1}
        accept="video/*,.mp4"
        disabled={!upload?._id || precheckLoading || !uploadAllowed}
        fileList={uploadFileList}
        beforeUpload={(file) => {
          if (precheckLoading || !uploadAllowed) {
            message.warning(
              "Upload is only allowed when there is no active job for this series (or after a failed run)."
            );
            return false;
          }
          setVideoFile(file);
          setFilename(file.name);
          return false;
        }}
        onRemove={() => {
          setVideoFile(null);
          setFilename("");
          return true;
        }}
        style={{
          opacity: upload?._id && uploadAllowed && !precheckLoading ? 1 : 0.55,
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ color: "#eb2f96", fontSize: 48 }} />
        </p>
        <p className="ant-upload-text" style={{ fontSize: 15 }}>
          Click or drag video into this area
        </p>
        <p className="ant-upload-hint">One video file per job</p>
      </Upload.Dragger>

      {filename ? (
        <Typography.Text type="secondary">
          Selected: <Typography.Text strong>{filename}</Typography.Text>
        </Typography.Text>
      ) : null}

      <Spin spinning={isUploading}>
        <Button
          className="vst-interactive-btn"
          type="primary"
          size="large"
          icon={<CloudUploadOutlined />}
          onClick={onUploadAndStart}
          disabled={uploadDisabled}
          block
        >
          {isUploading ? "Uploading…" : "Upload & Start Transcoding"}
        </Button>
      </Spin>
    </Space>
  );

  const jobMetaBlock = jobId ? (
    <>
      <div className="vst-meta-row">
        <Typography.Text type="secondary" className="vst-meta-label">
          Job ID
        </Typography.Text>
        <div className="vst-meta-value">
          <Typography.Text
            code
            copyable
            ellipsis={{ tooltip: jobId }}
            style={{ display: "block", width: "100%" }}
          >
            {jobId}
          </Typography.Text>
        </div>
      </div>

      {status != null ? (
        <div className="vst-meta-row">
          <Typography.Text type="secondary" className="vst-meta-label">
            Status
          </Typography.Text>
          <div className="vst-meta-value" style={{ overflow: "hidden" }}>
            <Tag
              color={getStatusTagColor(status)}
              style={{
                fontSize: 13,
                padding: "4px 12px",
                margin: 0,
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {String(status).toUpperCase()}
            </Tag>
          </div>
        </div>
      ) : null}
    </>
  ) : null;

  const killBlock = killButtonVisible ? (
    <Popconfirm
      title="Stop this transcoding job?"
      description="Sends a kill request to the transcoding API."
      okText="Stop"
      okButtonProps={{
        danger: true,
        loading: isKilling,
        className: "vst-interactive-btn",
      }}
      cancelText="Cancel"
      cancelButtonProps={{ className: "vst-interactive-btn" }}
      onConfirm={onKillJob}
    >
      <Button
        className="vst-interactive-btn"
        danger
        size="large"
        icon={<StopOutlined />}
        disabled={isKilling}
        block
      >
        Stop Transcoding
      </Button>
    </Popconfirm>
  ) : null;

  const renderContent = () => {
    switch (uiState.type) {
      case "COMPLETED":
        return (
          <Card
            className="vst-interactive-card vst-state-card--success"
            title="Transcoding completed"
            bodyStyle={{ padding: "20px 24px" }}
          >
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              {jobMetaBlock}
              {humanizedStep ? (
                <div>
                  <Typography.Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Final step
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 15 }}>{humanizedStep}</Typography.Text>
                </div>
              ) : null}
              <div className="vst-progress-wrap-success">
                <Progress
                  percent={100}
                  status="success"
                  strokeLinecap="round"
                  showInfo
                  format={() => "100%"}
                />
                <div className="vst-progress-shimmer-success" aria-hidden />
                <div className="vst-progress-shimmer-success-extra" aria-hidden />
              </div>
              <Alert
                type="success"
                showIcon
                message="Output directory"
                description={
                  <Typography.Paragraph copyable style={{ marginBottom: 0 }}>
                    {outputDir || "—"}
                  </Typography.Paragraph>
                }
              />
            </Space>
          </Card>
        );

      case "RUNNING":
        return (
          <Card
            className="vst-interactive-card"
            title="Transcoding in progress"
            bodyStyle={{ padding: "20px 24px" }}
          >
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Alert
                type="info"
                showIcon
                message="This series is being transcoded."
                description="Progress updates live below. You can stop the job if needed."
              />
              {jobMetaBlock}
              {humanizedStep ? (
                <div>
                  <Typography.Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 6 }}
                  >
                    Current step
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 15 }}>{humanizedStep}</Typography.Text>
                </div>
              ) : (
                <Typography.Text type="secondary">Preparing transcoding…</Typography.Text>
              )}
              <div className="vst-progress-wrap is-active">
                <Progress
                  percent={typeof progress === "number" ? progress : 0}
                  status="active"
                  strokeLinecap="round"
                  strokeColor="#1890ff"
                  showInfo
                  format={(pct) => `${Math.round(pct)}%`}
                />
                <div className="vst-progress-shimmer" aria-hidden />
                <div className="vst-progress-shimmer-extra" aria-hidden />
                <div className="vst-progress-sheen" aria-hidden />
                <div className="vst-progress-sparkle" aria-hidden />
              </div>
              {killBlock}
            </Space>
          </Card>
        );

      case "QUEUED":
        return (
          <Card
            className="vst-interactive-card"
            title="Job in queue"
            bodyStyle={{ padding: "20px 24px" }}
          >
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Alert
                type="warning"
                showIcon
                message="Waiting for processing"
                description="This series already has a queued job. Upload stays disabled until it finishes or is stopped."
              />
              {jobMetaBlock}
              <div className="vst-progress-wrap is-active">
                <Progress
                  percent={typeof progress === "number" ? progress : 0}
                  status="active"
                  strokeLinecap="round"
                  strokeColor="#1890ff"
                  showInfo
                  format={(pct) => `${Math.round(pct)}%`}
                />
                <div className="vst-progress-shimmer" aria-hidden />
                <div className="vst-progress-shimmer-extra" aria-hidden />
                <div className="vst-progress-sheen" aria-hidden />
                <div className="vst-progress-sparkle" aria-hidden />
              </div>
              {killBlock}
            </Space>
          </Card>
        );

      case "FAILED":
        return (
          <Card
            className="vst-interactive-card vst-state-card--error"
            title="Transcoding needs attention"
            bodyStyle={{ padding: "20px 24px" }}
          >
            <Space direction="vertical" size={20} style={{ width: "100%" }}>
              {jobMetaBlock}
              {statusUpper === "KILLED" ? (
                <Alert type="info" showIcon message="This job was cancelled." />
              ) : (
                <Alert
                  type="error"
                  showIcon
                  message="Previous transcoding did not finish"
                  description={
                    error && error !== ""
                      ? error
                      : "You can upload a new file below to start again."
                  }
                />
              )}
              <Typography.Title level={5} style={{ margin: 0 }}>
                Retry upload
              </Typography.Title>
              {uploadFormInner}
            </Space>
          </Card>
        );

      case "NEW":
      default:
        return (
          <Card
            className="vst-interactive-card"
            title="Upload video"
            bodyStyle={{ padding: "20px 24px" }}
          >
            {uploadFormInner}
          </Card>
        );
    }
  };

  return (
    <div className="transcoding-container">
      <style>{VST_UI_CSS}</style>
      <Space align="center" style={{ marginBottom: 20 }}>
        <ThunderboltOutlined style={{ fontSize: 22, color: "#eb2f96" }} />
        <Typography.Title level={4} style={{ margin: 0 }}>
          Vertical Shorts Transcoding
        </Typography.Title>
      </Space>
      {renderContent()}
    </div>
  );
};

export default VerticalShortTranscodingUpload;
